<template>
  <Dropdown
    :input-id="inputId"
    :options="options"
  >
    <template #default="{ displayValue, hasError, errors, onChange: onFieldChange }">
      <div class="w-60">
        <Combobox
          v-model="selected"
          @update:model-value="onChange($event, onFieldChange)"
        >
          <ComboboxLabel
            v-if="label"
            class="vp-label"
            :for="inputRef?.el?.id"
          >
            {{ label }}
          </ComboboxLabel>
          <div class="relative">
            <div
              class="relative w-full cursor-default overflow-hidden rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm"
            >
              <ComboboxInput
                ref="inputRef"
                class="w-full bg-vp-base-100 hover:bg-vp-base-200 focus:bg-vp-base-200 py-2 pl-1 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                :display-value="() => displayValue"
                :placeholder="placeholder"
                @change="query = $event.target.value"
              />
              <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDown
                  class="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </ComboboxButton>
            </div>
            <TransitionRoot
              leave="transition ease-in duration-100"
              leave-from="opacity-100"
              leave-to="opacity-0"
              @after-leave="query = ''"
            >
              <ComboboxOptions
                class="absolute mt-1 max-h-52 w-full overflow-auto shadow-sm rounded-md bg-white py-1 text-base sm:text-sm"
              >
                <div
                  v-if="filteredOptions.length === 0 && query !== ''"
                  class="relative cursor-default select-none px-4 py-2 text-gray-700"
                >
                  Nothing found.
                </div>

                <ComboboxOption
                  v-for="option in filteredOptions"
                  :key="option.value"
                  v-slot="{ selected, active }"
                  as="template"
                  :value="option"
                >
                  <li
                    class="relative cursor-default select-none py-2 pl-10 pr-4"
                    :class="{
                      'bg-vp-primary text-white': active,
                      'text-gray-900': !active,
                    }"
                  >
                    <span
                      class="block truncate"
                      :class="{ 'font-medium': selected, 'font-normal': !selected }"
                    >
                      {{ option.name }}
                    </span>
                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3"
                      :class="{ 'text-white': active, 'text-vp-primary': !active }"
                    >
                      <Check
                        class="h-5 w-5"
                        aria-hidden="true"
                      />
                    </span>
                  </li>
                </ComboboxOption>
              </ComboboxOptions>
            </TransitionRoot>
          </div>
        </Combobox>
      </div>
      <span
        v-if="hasError"
        class="text-vp-error font-bold text-xs"
      >{{ errors.join(', ') }}</span>
    </template>
  </Dropdown>
</template>
<script setup lang="ts">
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
  ComboboxLabel,
  TransitionRoot,
} from '@headlessui/vue';
import { ref } from 'vue';
import { ChevronDown, Check } from 'mdue';
import { Dropdown } from 'vuniform';
import { computed } from 'vue';
const emit = defineEmits<{
  (e: 'change', value?: unknown): void;
  (e: 'clear'): void;
}>();

interface Props {
  inputId: string;
  options?: { value: string; name: string }[];
  defaultValue?: string;
  clearable?: boolean;
  errors?: string[];
  forceError?: boolean;
  multiple?: boolean;
  placeholder?: string;
  clearText?: string;
  overrideValue?: string;
  label?: string;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  clearText: 'Clear',
  options: () => [],
  errors: () => [],
});

const inputRef = ref();
const selected = ref(props.options[0]);
const query = ref('');

const onChange = (selectedOption: { value: string; name: string }, cb: (value?: unknown) => void) => {
  const selectedValue = selectedOption.value;
  if (cb) {
    cb(selectedValue);
  }
  emit('change', selectedValue);
};

const filteredOptions = computed(() =>
  query.value === ''
    ? props.options
    : props.options.filter(option =>
      option.name.toLowerCase().replace(/\s+/g, '').includes(query.value.toLowerCase().replace(/\s+/g, '')),
    ),
);
</script>
